import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { FormValidation } from '@core/services/form-validation';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-input-text',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './input-text.html',
  styleUrls: ['./input-text.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputText),
      multi: true,
    },
  ],
})
export class InputText implements ControlValueAccessor, OnChanges, OnDestroy, AfterViewInit {
  @Input() type: string = 'text';
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() formControlName: string = '';
  /** When set, keeps the visible value in sync when the control updates outside typing (e.g. patch/reset). */
  @Input() control: AbstractControl | null = null;
  @Input() pattern: string = '';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() m: number = 0;
  /** When false (default), leading spaces are stripped on each input (email/login UX). */
  @Input() allowLeadingWhitespace = false;

  @Input() disable: boolean = false;

  @ViewChild('fieldInput', { read: ElementRef }) fieldInput?: ElementRef<HTMLInputElement>;

  value: string = '';
  disabled = false;

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  private readonly cdr = inject(ChangeDetectorRef);
  private controlValueSub: Subscription | null = null;

  constructor(public formValidation: FormValidation) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control']) {
      this.bindControlValueStream();
    }
  }

  ngAfterViewInit(): void {
    this.applyValueToInput();
  }

  ngOnDestroy(): void {
    this.controlValueSub?.unsubscribe();
  }

  private bindControlValueStream(): void {
    this.controlValueSub?.unsubscribe();
    this.controlValueSub = null;
    if (!this.control) {
      return;
    }
    this.value = this.normalizeModelValue(this.control.value);
    this.controlValueSub = this.control.valueChanges.subscribe((v) => {
      this.value = this.normalizeModelValue(v);
      this.applyValueToInput();
      this.cdr.markForCheck();
    });
  }

  private normalizeModelValue(v: unknown): string {
    return v == null ? '' : String(v);
  }

  /** Do not use [value] binding — it re-applies on every CD and can undo leading-space fixes. */
  private applyValueToInput(): void {
    const el = this.fieldInput?.nativeElement;
    if (!el) {
      return;
    }
    if (el.value !== this.value) {
      el.value = this.value;
    }
  }

  writeValue(value: string | null): void {
    this.value = this.normalizeModelValue(value);
    this.applyValueToInput();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    let raw = el.value;
    if (!this.allowLeadingWhitespace) {
      const normalized = raw.replace(/^\s+/, '');
      if (normalized !== raw) {
        const sel = el.selectionStart ?? raw.length;
        const removed = raw.length - normalized.length;
        el.value = normalized;
        const pos = Math.min(Math.max(0, sel - removed), normalized.length);
        el.setSelectionRange(pos, pos);
        raw = normalized;
      }
    }
    this.value = raw;
    this.onChange(raw);
    this.onTouched();
  }

  onBlurField(event: FocusEvent): void {
    const el = event.target as HTMLInputElement;
    let next = el.value ?? '';
    if (this.type === 'email') {
      next = next.trim();
    }
    if (next !== el.value) {
      el.value = next;
    }
    if (next !== this.value) {
      this.value = next;
      this.onChange(next);
    }
    this.onTouched();
  }

  get errorMessage(): string | null {
    if (this.control?.touched && this.control?.invalid) {
      return this.formValidation.getValidationMessage(this.control, this.formControlName);
    }
    return null;
  }

  get isInvalid(): boolean {
    return (this.control?.invalid && this.control?.touched) || this.disable || false;
  }

  get required(): boolean {
    if (!this.control || !this.control.validator) return false;
    const validator = this.control.validator({} as AbstractControl);
    return !!validator?.['required'];
  }
}
